import { useMemo } from 'react'

import { addressesEquivalent } from '@/data/marketApi/normalizeMarketToken'
import type { ChainId } from '../../sdk'
import { Currency, Token, Price } from '../../sdk'
import { PairState, usePair } from '../../data/Reserves'
import { wrappedCurrency } from '../../utils/wrappedCurrency'

export type OnChainPairData = {
  loading: boolean
  exists: boolean
  reserve0: string
  reserve1: string
  price: string
  priceInverted: string
  /** Cuánto token1 por 1 token0 (solo número). */
  rate1Per0: string
  /** Cuánto token0 por 1 token1 (solo número). */
  rate0Per1: string
  token0Symbol: string
  token1Symbol: string
  token0Address: string | null
  token1Address: string | null
  pairAddress: string | null
  feeTier: string
}

const emptyPairData = (
  loading: boolean,
  token0Symbol: string,
  token1Symbol: string,
): OnChainPairData => ({
  loading,
  exists: false,
  reserve0: '—',
  reserve1: '—',
  price: '—',
  priceInverted: '—',
  rate1Per0: '—',
  rate0Per1: '—',
  token0Symbol,
  token1Symbol,
  token0Address: null,
  token1Address: null,
  pairAddress: null,
  feeTier: '0.30%',
})

/** Importe spot en unidad receive por 1 unidad de pay (solo cantidad + símbolo). */
export type DirectedSpotRate = {
  amount: string
  unitSymbol: string
}

/** Partes de cotización «1 PAY = X RECEIVE» para render estructurado (cantidad vs unidad). */
export type DirectedSpotQuote = {
  paySymbol: string
  rateAmount: string
  rateUnitSymbol: string
}

function resolveDirectedSpotRate(
  pairData: OnChainPairData,
  pay: Currency | null | undefined,
  receive: Currency | null | undefined,
  chainId: ChainId,
): DirectedSpotRate | null {
  if (!pairData.exists || !pay || !receive) return null
  if (!pairData.token0Address || !pairData.token1Address) return null
  if (pairData.rate1Per0 === '—' || pairData.rate0Per1 === '—') return null

  const payWrapped = wrappedCurrency(pay, chainId)?.address.toLowerCase()
  const receiveWrapped = wrappedCurrency(receive, chainId)?.address.toLowerCase()
  const token0 = pairData.token0Address.toLowerCase()
  const token1 = pairData.token1Address.toLowerCase()

  if (!payWrapped || !receiveWrapped) return null

  const unitSymbol = receive.symbol ?? '—'

  if (payWrapped === token0 && receiveWrapped === token1) {
    return { amount: pairData.rate1Per0, unitSymbol }
  }
  if (payWrapped === token1 && receiveWrapped === token0) {
    return { amount: pairData.rate0Per1, unitSymbol }
  }

  return null
}

export function getDirectedSpotRate(
  pairData: OnChainPairData,
  pay: Currency | null | undefined,
  receive: Currency | null | undefined,
  chainId: ChainId,
): DirectedSpotRate | null {
  return resolveDirectedSpotRate(pairData, pay, receive, chainId)
}

export function getDirectedSpotQuote(
  pairData: OnChainPairData,
  pay: Currency | null | undefined,
  receive: Currency | null | undefined,
  chainId: ChainId,
): DirectedSpotQuote | null {
  const rate = resolveDirectedSpotRate(pairData, pay, receive, chainId)
  if (!rate || !pay) return null
  return {
    paySymbol: pay.symbol ?? '—',
    rateAmount: rate.amount,
    rateUnitSymbol: rate.unitSymbol,
  }
}

function legMatchesOnChainToken(legAddress: string, tokenAddress: string, chainId: ChainId): boolean {
  return addressesEquivalent(legAddress, tokenAddress, chainId)
}

/** Precio fijo base/quote del índice de mercado (no sigue el sentido del swap). */
export function getBaseQuoteSpotRate(
  pairData: OnChainPairData,
  base: { address: string; symbol: string },
  quote: { address: string; symbol: string },
  chainId: ChainId,
): DirectedSpotRate | null {
  if (!pairData.exists || !pairData.token0Address || !pairData.token1Address) return null
  if (pairData.rate1Per0 === '—' || pairData.rate0Per1 === '—') return null

  const token0 = pairData.token0Address
  const token1 = pairData.token1Address

  if (legMatchesOnChainToken(base.address, token0, chainId) && legMatchesOnChainToken(quote.address, token1, chainId)) {
    return { amount: pairData.rate1Per0, unitSymbol: quote.symbol }
  }
  if (legMatchesOnChainToken(base.address, token1, chainId) && legMatchesOnChainToken(quote.address, token0, chainId)) {
    return { amount: pairData.rate0Per1, unitSymbol: quote.symbol }
  }

  return null
}

/** Cotización «1 PAY = X RECEIVE» según el sentido del swap (match por dirección, no símbolo). */
export function formatDirectedSpotQuote(
  pairData: OnChainPairData,
  pay: Currency | null | undefined,
  receive: Currency | null | undefined,
  chainId: ChainId,
): string | null {
  const quote = getDirectedSpotQuote(pairData, pay, receive, chainId)
  if (!quote) return null
  return `1 ${quote.paySymbol} = ${quote.rateAmount} ${quote.rateUnitSymbol}`
}

export function useOnChainPairData(
  currencyA?: Currency | null,
  currencyB?: Currency | null,
): OnChainPairData {
  const [pairState, pair] = usePair(currencyA ?? undefined, currencyB ?? undefined)

  return useMemo(() => {
    const token0Symbol = pair?.token0?.symbol ?? currencyA?.symbol ?? '—'
    const token1Symbol = pair?.token1?.symbol ?? currencyB?.symbol ?? '—'
    const loading = pairState === PairState.LOADING
    const exists = pairState === PairState.EXISTS && pair != null

    if (!exists || !pair) {
      return emptyPairData(loading, token0Symbol, token1Symbol)
    }

    const reserve0 = pair.reserve0.toSignificant(6)
    const reserve1 = pair.reserve1.toSignificant(6)
    const token0Address = pair.token0.address
    const token1Address = pair.token1.address

    let price = '—'
    let priceInverted = '—'
    let rate1Per0 = '—'
    let rate0Per1 = '—'
    try {
      const p = new Price(pair.token0, pair.token1, pair.reserve0.raw, pair.reserve1.raw)
      rate1Per0 = p.toSignificant(6)
      rate0Per1 = p.invert().toSignificant(6)
      price = `${rate1Per0} ${token1Symbol}`
      priceInverted = `${rate0Per1} ${token0Symbol}`
    } catch {
      /* reserves may be zero */
    }

    const pairAddress =
      pair.liquidityToken instanceof Token ? pair.liquidityToken.address : null

    return {
      loading,
      exists: true,
      reserve0,
      reserve1,
      price,
      priceInverted,
      rate1Per0,
      rate0Per1,
      token0Symbol,
      token1Symbol,
      token0Address,
      token1Address,
      pairAddress,
      feeTier: '0.30%',
    }
  }, [pair, pairState, currencyA?.symbol, currencyB?.symbol])
}
